import {
  RouterProvider,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { toast } from '@/lib/toast';
import { breadcrumbs } from '@/lib/breadcrumbs';
import { trackPageView } from '@/lib/metrics';
import { useEffect, useMemo, lazy, Suspense } from 'react';
import type { JSX } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AppShell, type NavItem } from '@/components/blocks/chrome';
import type { IconName } from '@/components/ui/icon';
import { FeatureErrorBoundary } from '@/components/blocks/feedback/FeatureErrorBoundary';
import { DatabaseLoadingState, DatabaseErrorState } from '@/components/blocks/feedback';
import { SeedData } from '../data/reports/seedData';
import { isDataResetToolsEnabled } from '../lib/build-info';
import { ensureDevDataFreshness } from '../data/ensureDevDataFreshness';
import { ensureDefaults } from '../data/ensureDefaults';
import { getAppModeSync } from '../lib/appMode';
import { ModePicker } from '../components/blocks/feedback/ModePicker';
import { useDatabaseReady } from '../data/useDatabaseReady';
import { GlobalUiRouteSync } from './GlobalUiRouteSync';
import { pickUiSearch } from './uiSearch';
import {
  CompanyDetailGuard,
  RefrigerationUnitDetailGuard,
  ReportDetailGuard,
  SiteDetailGuard,
} from './routeGuards';

// Lazy-loaded route components for code splitting
const HomeScreen = lazy(() => import('../features/home').then((m) => ({ default: m.HomeScreen })));
const CompaniesListScreen = lazy(() =>
  import('../features/companies').then((m) => ({
    default: m.CompaniesListScreen,
  }))
);
const ReportsListScreen = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.ReportsListScreen }))
);
const ReportSectionScreen = lazy(() =>
  import('../features/reports').then((m) => ({
    default: m.ReportSectionScreen,
  }))
);
const CalendarGridScreen = lazy(() =>
  import('../features/calendar').then((m) => ({
    default: m.CalendarGridScreen,
  }))
);
const PlanningScreen = lazy(() =>
  import('../features/calendar').then((m) => ({ default: m.PlanningScreen }))
);
const ActivitiesHubScreen = lazy(() =>
  import('../features/calendar').then((m) => ({
    default: m.ActivitiesHubScreen,
  }))
);
const SettingsLayout = lazy(() =>
  import('../features/settings').then((m) => ({ default: m.SettingsLayout }))
);
const SettingsScreen = lazy(() =>
  import('../features/settings').then((m) => ({ default: m.SettingsScreen }))
);
const SettingsDataScreen = lazy(() =>
  import('../features/settings').then((m) => ({
    default: m.SettingsDataScreen,
  }))
);
const SettingsCatalogsScreen = lazy(() =>
  import('../features/settings').then((m) => ({
    default: m.SettingsCatalogsScreen,
  }))
);
const SettingsGuideScreen = lazy(() =>
  import('../features/settings').then((m) => ({
    default: m.SettingsGuideScreen,
  }))
);
const SettingsTemplatesScreen = lazy(() =>
  import('../features/settings').then((m) => ({
    default: m.SettingsTemplatesScreen,
  }))
);
const SettingsDevelopmentScreen = lazy(() =>
  import('../features/settings').then((m) => ({
    default: m.SettingsDevelopmentScreen,
  }))
);
const SettingsSupportScreen = lazy(() =>
  import('../features/settings').then((m) => ({
    default: m.SettingsSupportScreen,
  }))
);
const ChangelogScreen = lazy(() =>
  import('../features/changelog').then((m) => ({ default: m.ChangelogScreen }))
);

// Loading fallback for lazy routes
function RouteLoadingFallback(): JSX.Element {
  return (
    <div className="flex h-full min-h-[200px] items-center justify-center">
      <div className="text-text-secondary">Caricamento...</div>
    </div>
  );
}

const navIcons: Record<string, IconName> = {
  home: 'home',
  companies: 'corporate_fare',
  reports: 'assignment_turned_in',
  calendar: 'event',
  planning: 'schedule',
  activities: 'square_check_big',
  settings: 'settings',
  guide: 'info',
  changelog: 'history',
};

const navToPath: Record<string, string> = {
  home: '/home',
  companies: '/companies',
  reports: '/reports',
  calendar: '/calendar',
  planning: '/planning',
  activities: '/activities',
  guide: '/guide',
  changelog: '/changelog',
};

const rootRoute = createRootRoute({
  component: RootLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/home' });
  },
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/home',
  component: HomeScreen,
});

const companiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/companies',
  component: CompaniesListScreen,
});

const companyDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/companies/$companyId',
  component: CompanyDetailGuard,
  validateSearch: (search: Record<string, unknown>) => ({
    ...pickUiSearch(search),
    tab: typeof search.tab === 'string' ? search.tab : undefined,
  }),
});

const siteDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/companies/$companyId/sites/$siteId',
  component: SiteDetailGuard,
  validateSearch: (search: Record<string, unknown>) => ({
    ...pickUiSearch(search),
    tab: typeof search.tab === 'string' ? search.tab : undefined,
  }),
});

const unitDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/companies/$companyId/sites/$siteId/units/$unitId',
  component: RefrigerationUnitDetailGuard,
  validateSearch: (search: Record<string, unknown>) => ({
    ...pickUiSearch(search),
    tab: typeof search.tab === 'string' ? search.tab : undefined,
    safety: typeof search.safety === 'string' ? search.safety : undefined,
  }),
});

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports',
  component: ReportsListScreen,
  validateSearch: (search: Record<string, unknown>) => ({
    ...pickUiSearch(search),
    query: typeof search.query === 'string' ? search.query : undefined,
  }),
});

type UiSearchParams = ReturnType<typeof pickUiSearch>;

type ReportSearchParams = UiSearchParams & {
  companyId?: string;
  siteId?: string;
  mode?: 'edit';
  section?: string;
  subsection?: string;
};

const sanitizeReportSearch = (search: Record<string, unknown>): ReportSearchParams => {
  const result: ReportSearchParams = {
    ...pickUiSearch(search),
  };
  if (typeof search.companyId === 'string') result.companyId = search.companyId;
  if (typeof search.siteId === 'string') result.siteId = search.siteId;
  if (search.mode === 'edit') result.mode = 'edit';
  if (typeof search.section === 'string') result.section = search.section;
  if (typeof search.subsection === 'string') {
    result.subsection = search.subsection;
  }
  return result;
};

const reportDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/$reportId',
  component: ReportDetailGuard,
  validateSearch: sanitizeReportSearch,
});

const reportDocumentRoute = createRoute({
  getParentRoute: () => reportDetailRoute,
  path: '/documento',
  component: ReportSectionScreen,
});

const reportDefaultRoute = createRoute({
  getParentRoute: () => reportDetailRoute,
  path: '/',
  beforeLoad: ({ params, search }) => {
    throw redirect({
      to: '/reports/$reportId/documento',
      params,
      search,
    });
  },
});

const calendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/calendar',
  component: CalendarGridScreen,
  validateSearch: (search: Record<string, unknown>) => ({
    ...pickUiSearch(search),
    query: typeof search.query === 'string' ? search.query : undefined,
    fromDate: typeof search.fromDate === 'string' ? search.fromDate : undefined,
    toDate: typeof search.toDate === 'string' ? search.toDate : undefined,
    types: Array.isArray(search.types)
      ? search.types.filter((value): value is string => typeof value === 'string')
      : typeof search.types === 'string'
        ? [search.types]
        : undefined,
    month:
      typeof search.month === 'number' || typeof search.month === 'string'
        ? Number.isFinite(Number(search.month))
          ? Number(search.month)
          : undefined
        : undefined,
    year:
      typeof search.year === 'number' || typeof search.year === 'string'
        ? Number.isFinite(Number(search.year))
          ? Number(search.year)
          : undefined
        : undefined,
    day:
      typeof search.day === 'number' || typeof search.day === 'string'
        ? Number.isFinite(Number(search.day))
          ? Number(search.day)
          : undefined
        : undefined,
    view: typeof search.view === 'string' && search.view === 'week' ? 'week' : undefined,
  }),
});

const planningRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning',
  component: PlanningScreen,
});

const activitiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/activities',
  component: ActivitiesHubScreen,
  validateSearch: (search: Record<string, unknown>) => ({
    ...pickUiSearch(search),
    query: typeof search.query === 'string' ? search.query : undefined,
    status: Array.isArray(search.status)
      ? search.status.filter((value): value is string => typeof value === 'string')
      : typeof search.status === 'string'
        ? [search.status]
        : undefined,
    companyId: typeof search.companyId === 'string' ? search.companyId : undefined,
    siteId: typeof search.siteId === 'string' ? search.siteId : undefined,
    reportId: typeof search.reportId === 'string' ? search.reportId : undefined,
  }),
});

const changelogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/changelog',
  component: ChangelogScreen,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsLayout,
});

const settingsGeneralRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/',
  component: SettingsScreen,
});

const settingsDataRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/data',
  component: SettingsDataScreen,
});

const settingsCatalogsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/catalogs',
  component: SettingsCatalogsScreen,
});

const settingsTemplatesRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/templates',
  component: SettingsTemplatesScreen,
});

const settingsDevelopmentRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/development',
  component: SettingsDevelopmentScreen,
});

const settingsSupportRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/support',
  component: SettingsSupportScreen,
});

const guideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guide',
  component: SettingsGuideScreen,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  homeRoute,
  companiesRoute,
  companyDetailRoute,
  siteDetailRoute,
  unitDetailRoute,
  reportsRoute,
  reportDetailRoute.addChildren([reportDefaultRoute, reportDocumentRoute]),
  calendarRoute,
  planningRoute,
  activitiesRoute,
  guideRoute,
  changelogRoute,
  settingsRoute.addChildren([
    settingsGeneralRoute,
    settingsDataRoute,
    settingsTemplatesRoute,
    settingsCatalogsRoute,
    settingsDevelopmentRoute,
    settingsSupportRoute,
  ]),
]);

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function RootLayout(): JSX.Element {
  // All hooks must be called unconditionally at the top
  const { isReady: dbReady, error: dbError } = useDatabaseReady();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const navigate = useNavigate({ from: '/' });
  const queryClient = useQueryClient();

  // Track navigation for error diagnostics and metrics
  useEffect(() => {
    breadcrumbs.navigation(pathname);
    trackPageView(pathname);
  }, [pathname]);

  // Initialize data on first load (only runs after db is ready)
  // - Demo mode: Seeds sample data if DB is empty
  // - Real mode: Only ensures essential defaults exist (template)
  useEffect(() => {
    if (!dbReady) return;

    // Add a small delay to avoid double-execution in React StrictMode
    const timeoutId = setTimeout(async () => {
      const appMode = getAppModeSync();

      try {
        // Always ensure defaults exist (template) regardless of mode
        await ensureDefaults();

        if (appMode === 'demo') {
          // Demo mode: check for dev data freshness and seed if needed
          const result = await ensureDevDataFreshness();

          if (result.resetTriggered) {
            toast.info(`Dati demo aggiornati (${result.expectedVersion})`);
          }

          if (isDataResetToolsEnabled()) {
            await SeedData.seedIfEmptyOnce();
          }
        }
        // Real mode: no seeding, just defaults (already done above)
      } catch (error) {
        console.error("[App] Errore durante l'inizializzazione dati:", error);
      }

      // Invalidate all queries so components re-fetch data
      // This ensures UI updates without requiring a page refresh
      void queryClient.invalidateQueries();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [queryClient, dbReady]);

  const navigationItems: NavItem[] = useMemo(
    () => [
      {
        id: 'home',
        label: 'Home',
        icon: navIcons.home,
        path: navToPath.home,
      },
      {
        id: 'companies',
        label: 'Aziende',
        icon: navIcons.companies,
        path: navToPath.companies,
      },
      {
        id: 'reports',
        label: 'Report',
        icon: navIcons.reports,
        path: navToPath.reports,
      },
      {
        id: 'calendar',
        label: 'Calendario',
        icon: navIcons.calendar,
        path: navToPath.calendar,
      },
      {
        id: 'planning',
        label: 'Ispezioni',
        icon: navIcons.planning,
        path: navToPath.planning,
      },
      {
        id: 'activities',
        label: 'Attività',
        icon: navIcons.activities,
        path: navToPath.activities,
      },
    ],
    []
  );

  const currentNavId = useMemo(() => {
    // Handle settings route specially since it's not in navToPath
    if (pathname.startsWith('/settings')) {
      return 'settings';
    }

    const entry = Object.entries(navToPath).find(([, path]) => pathname.startsWith(path));
    return entry ? entry[0] : 'companies';
  }, [pathname]);

  const baseBreadcrumbs = useMemo(() => [], []);

  // Gate: Wait for database migrations to complete before rendering main UI
  if (dbError) {
    return <DatabaseErrorState error={dbError} />;
  }

  if (!dbReady) {
    return <DatabaseLoadingState />;
  }

  return (
    <>
      {/* Mode picker dialog shown on first launch */}
      <ModePicker />
      <AppShell
        navigationItems={navigationItems}
        activeNavId={currentNavId}
        settingsPath="/settings"
        breadcrumbs={baseBreadcrumbs}
      >
        <GlobalUiRouteSync />
        <FeatureErrorBoundary
          featureName="Pagina"
          fallbackTitle="Errore di caricamento"
          fallbackMessage="Questa pagina ha riscontrato un errore. Riprova o torna alla home."
          onRetry={() => window.location.reload()}
          onClose={() => navigate({ to: '/home' })}
          closeLabel="Home"
        >
          <Suspense fallback={<RouteLoadingFallback />}>
            <Outlet />
          </Suspense>
        </FeatureErrorBoundary>
      </AppShell>
    </>
  );
}

export function AppRouterProvider(): JSX.Element {
  return <RouterProvider router={router} />;
}
